/**
 * Real-time booking notification system
 * Polls /api/bookings for new pending bookings every 30 seconds after login
 * Shows animated popup toasts for each new arrival
 */
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "./AuthContext";

const NotificationContext = createContext(null);
const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const POLL_INTERVAL = 30000; // 30 seconds

export function NotificationProvider({ children }) {
  const { token, authHeaders } = useAuth();
  const [notifications, setNotifications] = useState([]); // active toast queue
  const seenIds = useRef(new Set(JSON.parse(sessionStorage.getItem("seen_booking_ids") || "[]")));

  // Real notification feed (bell icon) — backed by the server, covers every
  // event type (bookings, payments, guest-form submissions, reviews), not
  // just pending bookings. `unreadCount` here is the source of truth for
  // the bell badge; it replaces the old client-only "seen pending bookings"
  // counter.
  const [feed, setFeed] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const persistSeen = useCallback(() => {
    sessionStorage.setItem("seen_booking_ids", JSON.stringify([...seenIds.current]));
  }, []);

  const dismiss = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const loadFeed = useCallback(async () => {
    if (!token) return;
    try {
      const res  = await fetch(`${API}/notifications?limit=30`, { headers: authHeaders() });
      if (!res.ok) return;
      const json = await res.json();
      setFeed(json.data || []);
      setUnreadCount(json.unreadCount ?? 0);
    } catch { /* silent */ }
  }, [token]);

  const markAllRead = useCallback(async () => {
    setUnreadCount(0);
    setFeed(prev => prev.map(n => ({ ...n, read: true })));
    try {
      await fetch(`${API}/notifications/read-all`, { method: "POST", headers: authHeaders() });
    } catch { /* silent — local state already reflects it, next poll reconciles */ }
  }, []);

  useEffect(() => {
    if (!token) return;
    loadFeed();
    const timer = setInterval(loadFeed, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [token, loadFeed]);

  // Poll for new pending bookings
  useEffect(() => {
    if (!token) return;

    async function poll() {
      try {
        const res  = await fetch(`${API}/bookings?status=Pending&limit=20`, {
          headers: authHeaders(),
        });
        if (!res.ok) return;
        const json = await res.json();
        const pending = json.data || [];

        const newOnes = pending.filter(b => !seenIds.current.has(b._id));
        if (newOnes.length === 0) return;

        newOnes.forEach(b => seenIds.current.add(b._id));
        persistSeen();
        // A brand-new pending booking also just created a "booking_new"
        // notification server-side — refresh the bell feed right away
        // instead of waiting up to POLL_INTERVAL for the next scheduled poll.
        loadFeed();

        // Push a toast for each new booking (max 3 at once)
        newOnes.slice(0, 3).forEach((b, i) => {
          const toast = {
            id:       `${b._id}-${Date.now()}-${i}`,
            bookingId: b._id,
            guestName: b.guest?.fullNames?.[0] || "New Guest",
            whatsapp:  b.guest?.whatsapp || "",
            checkIn:   b.checkIn,
            checkOut:  b.checkOut,
            source:    b.source,
            bookingRef:b.bookingRef,
            ts:        Date.now(),
          };

          // Stagger toasts slightly
          setTimeout(() => {
            setNotifications(prev => [toast, ...prev].slice(0, 5));
          }, i * 400);
        });
      } catch { /* silent */ }
    }

    poll(); // immediate on login
    const timer = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [token, loadFeed]);

  return (
    <NotificationContext.Provider value={{ notifications, dismiss, feed, unreadCount, markAllRead, refreshFeed: loadFeed }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
